import { Component, ViewChild, OnInit, inject } from '@angular/core';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { Observable, of } from 'rxjs';
import { switchMap, finalize, filter } from 'rxjs/operators';

import { FeatureFlagService, FeatureFlags } from '@core';
import { ModalService } from '@shared';

import { Patient, PatientService, PatientTaskService } from 'planner-shared';

import { PatientsComponent } from './patients/patients.component';
import { PatientAddressService } from './services/patient-address.service';
import { PatientsOverviewComponent } from './patients-overview/patients-overview.component';
import { PatientsTimeWindowSuggestionsComponent } from './patients-time-window-suggestions/patients-time-window-suggestions.component';
import { PatientInfoComponent } from './patient-info/patient-info.component';
import { PatientsTimeWindowSuggestionsService } from './patients-time-window-suggestions/services/patients-time-window-suggestions.service';

@UntilDestroy()
@Component({
    selector: 'app-data-update',
    templateUrl: './data-update.component.html',
    styleUrls: ['./data-update.component.scss'],
    standalone: false
})
export class DataUpdateComponent implements OnInit {
    private readonly modalService = inject(ModalService);
    private readonly patientService = inject(PatientService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly patientTaskService = inject(PatientTaskService);
    private readonly patientAddressService = inject(PatientAddressService);
    private readonly patientsTimeWindowSuggestionsService = inject(PatientsTimeWindowSuggestionsService);

    public patient: Patient;
    public timeWindowSuggestionsCount$: Observable<number>;
    @ViewChild(PatientsComponent) private readonly patientsComponent: PatientsComponent;
    @ViewChild(PatientInfoComponent) private patientInfoComponent: PatientInfoComponent;
    @ViewChild(PatientsOverviewComponent)
    private patientsOverviewComponent: PatientsOverviewComponent;

    ngOnInit(): void {
        this.initTimeWindowSuggestionsCount();

        this.patientTaskService
            .getPatientTaskEditStateChange()
            .pipe(
                untilDestroyed(this),
                filter(patient => patient != null),
                switchMap(patient => this.reloadPatientFeasibility(patient))
            )
            .subscribe(() => {
                this.patientInfoComponent.reloadPatientTasks();
            });

        this.patientAddressService
            .getPatientAddressEditStateChange()
            .pipe(
                untilDestroyed(this),
                filter(patient => patient != null),
                switchMap(patient => this.reloadPatientFeasibility(patient))
            )
            .subscribe();
    }

    public selectPatient(patient: Patient): void {
        this.patient = patient;
    }

    public openTimeWindowSuggestionsModal(): void {
        this.modalService.openCustomModal(
            PatientsTimeWindowSuggestionsComponent,
            () => this.initTimeWindowSuggestionsCount(),
            null,
            null,
            true
        );
    }

    private reloadPatientFeasibility(patient: Patient): Observable<void> {
        patient.feasibilityReloading = true;
        return this.patientService.reloadPatientFeasibility(patient).pipe(
            switchMap(() => {
                this.patientsComponent.resortPatients();
                this.patientInfoComponent.reloadPatientTasksFeasibilityInfo(patient.patientId);
                return this.patientsOverviewComponent.loadPatientTasksInfoCount();
            }),
            finalize(() => (patient.feasibilityReloading = false))
        );
    }

    private initTimeWindowSuggestionsCount(): void {
        this.timeWindowSuggestionsCount$ = this.featureFlagService.isEnabled(FeatureFlags.TimeWindowSuggestions).pipe(
            switchMap(enabled => {
                if (enabled) {
                    return this.patientsTimeWindowSuggestionsService
                        .getTimeWindowSuggestionsCount()
                        .pipe(untilDestroyed(this));
                }
                return of(0);
            })
        );
    }
}
